import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from '@innovation-os-ng/core/auth';
import { LogService } from '@innovation-os-ng/core/logger';
import { UserDetails } from '@innovation-os-ng/core/models';
import { skipper, TenantSelectors } from '@innovation-os-ng/core/state';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, ReplaySubject, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { TenantUserDetailsService } from '../contracts/tenant-user-details.service';
import { StringMap } from '../types/string-map.types';

export class TenantUserDetailsException {
  constructor(protected exception?: ApiResponse) {}
}

const TENANT_USER_DETAILS_ERRORS: {
  [errorCode: string]: typeof TenantUserDetailsException;
} = {};

@Injectable()
/**
 * Provides methods to retrieve information about tenant users.
 */
export class ApiTenantUserDetailsService implements TenantUserDetailsService {
  private readonly restUrl: string;
  /**
   * User Cache Map Structure => { tenantUri1_userUri1 : Subject<UserDetails>, .....}
   * Description
   * * Key   : Combination of tenant uri and user uri separated by "_".
   * * value : Subject of UserDetails
   *
   */
  private readonly cachedUsersDetails: StringMap<Subject<UserDetails>> = {};

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly httpClient: HttpClient,
    private readonly logger: LogService,
    private readonly store: Store
  ) {
    this.restUrl = this.environmentService.getRestUrl();
  }

  /**
   * Returns tenant user detail for given user uri
   *
   * Note:
   * * tenantUri is optional here if not provided the current tenant is used
   *
   * @param userUri User uri for which details is to be fetched.
   * @param tenantUri tenant uri the user related to
   */

  public async getUser(userUri: string, tenantUri?: string): Promise<UserDetails> {
    this.logger.warn('This method is deprecated! Please use the user.service in core');
    try {
      tenantUri = tenantUri || (await firstValueFrom(this.store.select(TenantSelectors.selectCurrentTenant).pipe(skipper())))?.tenantUri
      console.assert(!!tenantUri, 'tenantUri should not be null')
      const chacheKey = this.getCacheKey(userUri, tenantUri);
      if (!this.cachedUsersDetails[chacheKey]) {
        this.cachedUsersDetails[chacheKey] = new ReplaySubject<UserDetails>(1);
        const userDetails = await firstValueFrom(
          this.httpClient.get<UserDetails>(`${this.restUrl}/users/tenantUsers/${tenantUri}/${userUri}`)
        );
        this.cachedUsersDetails[chacheKey].next(userDetails);
      }
      return firstValueFrom(this.cachedUsersDetails[chacheKey]);
    } catch (error) {
      this.throwOnErrors(error);
    }
  }

  /**
   * Returns multiple users details for a tenant.
   *
   * Note:
   * * tenantUri is optional here if not provided the current tenant is used
   *
   * @param uris The user uri's of the user's for which the details
   * are to be fetched.
   * @param tenantUri tenant uri which the users are related to
   */

  public async getUsers(uris: Array<string>, tenantUri?: string): Promise<Array<UserDetails>> {
    this.logger.warn('This method is deprecated! Please use the user.service in core');
    tenantUri = tenantUri || (await firstValueFrom(this.store.select(TenantSelectors.selectCurrentTenant).pipe(skipper())))?.tenantUri
    console.assert(!!tenantUri, 'tenantUri should not be null')
    uris = uris.filter((uri, index) => uris.indexOf(uri) === index);

    const urisNotCached: Array<string> = this.getUsersUrisThatAreNotCached(uris, tenantUri);

    this.createStreamsForUrisThatAreNotCached(urisNotCached, tenantUri);

    if (urisNotCached.length) {
      return firstValueFrom(
        this.fetchMulitpleUserDetails(urisNotCached, tenantUri).pipe(
          tap((usersDetails) => {
            usersDetails.forEach((userDetails) => {
              const userDetailsSubject = this.cachedUsersDetails[this.getCacheKey(userDetails.userUri, tenantUri)];
              if (userDetailsSubject) {
                userDetailsSubject.next(userDetails);
              }
            });
          }),
          switchMap((_) => combineLatest(this.extractStreamsfromCache(uris, tenantUri)))
        )
      );
    }

    return firstValueFrom(combineLatest(this.extractStreamsfromCache(uris, tenantUri)));
  }

  private fetchMulitpleUserDetails(uris: Array<string>, tenantUri: string) {
    return this.httpClient.get<Array<UserDetails>>(`${this.restUrl}/tenants/tenants/${tenantUri}/users`, {
      params: {
        userUris: uris.join()
      }
    });
  }

  private createStreamsForUrisThatAreNotCached(uris: Array<string>, tenantUri: string): void {
    uris.forEach((uri) => (this.cachedUsersDetails[this.getCacheKey(uri, tenantUri)] = new ReplaySubject(1)));
  }

  private getUsersUrisThatAreNotCached(uris: Array<string>, tenantUri: string): Array<string> {
    return uris.filter((uri) => !this.cachedUsersDetails[this.getCacheKey(uri, tenantUri)]);
  }

  private extractStreamsfromCache(uris: Array<string>, tenantUri: string): Array<Subject<UserDetails>> {
    return uris.map((uri) => {
      const chacheKey = this.getCacheKey(uri, tenantUri);
      return this.cachedUsersDetails[chacheKey];
    });
  }

  private getCacheKey(userUri: string, tenantUri: string): string {
    return `${tenantUri}_${userUri}`;
  }

  /**
   *
   * @param response response from endpoints
   *
   */
  private throwOnErrors(response: ApiResponse): void {
    if (!(response instanceof HttpErrorResponse)) {
      throw response;
    }
    if (!response.error.error) {
      throw response;
    }
    const apiException = TENANT_USER_DETAILS_ERRORS[response.error.error];
    if (!apiException) {
      throw new Error(response.error.error);
    }

    throw new apiException(response);
  }
}
